import { Printer, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useStore } from "@/store/AppStore";
import { PageHeader, Panel, Tag, attTone } from "@/components/kit";
import { MonthReport } from "@/components/Attendance";
import { companySummary, ranking, rate } from "@/lib/selectors";
import { monthReport, fmtMin } from "@/lib/attendance";
import { MONTH_LABEL, MONTH_KEY } from "@/data/mock";
import { brl } from "@/lib/format";

const COLS = ["Empresa", "Segmento", "Vendas", "Meta", "% meta", "Equipe", "Tarefas", "Atrasadas", "Checklists", "Faltas", "Férias", "Atestados"];

const companyRow = (s, c) => {
  const r = companySummary(s, c);
  const reps = ranking(s, c.id).map((p) => monthReport(s, p));
  const total = (k) => reps.reduce((n, x) => n + x[k], 0);
  const checklists = rate(s.checklists.filter((cl) => cl.companyId === c.id).flatMap((cl) => cl.items));
  return { ...r, checklists, falta: total("falta"), ferias: total("ferias"), atestado: total("atestado") };
};

const downloadCsv = (s) => {
  const head = ["Empresa", "Colaboradora", "Função", "Dias trabalhados", "Horas", "Atrasos", "Faltas", "Férias", "Atestado", "Folga", "Tarefas %", "Meta vendas %", "Vendas (R$)", "Ranking"];
  const lines = s.companies.flatMap((c) =>
    ranking(s, c.id).map((p) => {
      const m = monthReport(s, p);
      return [c.name, p.name, p.role, m.days, fmtMin(m.minutes), m.late, m.falta, m.ferias, m.atestado, m.folga, p.tasks, p.att, p.sales, `${p.position}º`];
    }),
  );
  const csv = [head, ...lines].map((l) => l.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";")).join("\n");
  const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `relatorio-bella-gestao-${MONTH_KEY}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export default function MasterReport() {
  const { state } = useStore();
  const rows = state.companies.map((c) => companyRow(state, c));
  return (
    <div data-testid="master-report-page">
      <PageHeader
        eyebrow={MONTH_LABEL}
        title="Relatório mensal das empresas"
        subtitle="Resultado consolidado de todas as empresas atendidas e o desempenho de cada equipe."
        actions={
          <div className="flex flex-wrap gap-2 print:hidden">
            <Button variant="outline" onClick={() => downloadCsv(state)} data-testid="download-csv-button"><Download className="mr-1.5 h-4 w-4" /> Baixar planilha</Button>
            <Button className="font-semibold" onClick={() => window.print()} data-testid="print-all-button"><Printer className="mr-1.5 h-4 w-4" /> Imprimir / salvar PDF</Button>
          </div>
        }
      />
      <Panel title="Visão consolidada" subtitle={`${rows.length} empresas`} testId="consolidated-table">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-neutral-400">{COLS.map((h) => <th key={h} className="pb-3 pr-3 font-medium">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`consolidated-row-${r.id}`}>
                  <td className="py-3 pr-3 font-semibold">{r.name}</td>
                  <td className="pr-3 text-neutral-500">{r.segment}</td>
                  <td className="pr-3 font-semibold">{brl(r.sales)}</td>
                  <td className="pr-3">{brl(r.salesGoal)}</td>
                  <td className="pr-3"><Tag tone={attTone(r.att)}>{r.att}%</Tag></td>
                  <td className="pr-3">{r.team}</td>
                  <td className="pr-3">{r.taskRate}%</td>
                  <td className={`pr-3 ${r.overdue ? "font-semibold text-red-600" : ""}`}>{r.overdue}</td>
                  <td className="pr-3">{r.checklists}%</td>
                  <td className={`pr-3 ${r.falta ? "font-semibold text-red-600" : ""}`}>{r.falta}</td>
                  <td className="pr-3">{r.ferias}</td>
                  <td className="pr-3">{r.atestado}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      {state.companies.map((c) => (
        <div key={c.id} className="mt-8 print:break-before-page">
          <MonthReport companyId={c.id} hidePrint title={`${c.name} · ${MONTH_LABEL}`} />
        </div>
      ))}
    </div>
  );
}
